import { useFormik } from "formik";
import { ReactElement, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  hasRoleAccessAtLeastOneOfConstraint,
  isRoleAccessFieldEnabled,
  isRoleAccessFieldRequired,
} from "../../../../helper/roleAccess";
import { IdentificationMethodEnum, isNoSignInMethod, isPasswordSignInMethod } from "../../../../types/profile";
import Container from "../../../atoms/Container";
import InscriptionFields from "../../../molecules/Home/Inscription/InscriptionFields";
import ContactFields from "../../../molecules/Home/Inscription/ContactFields";
import InscriptionIdentifier from "../../../molecules/Home/Inscription/InscriptionIdentifier";
import NextPrevButtons from "../../../molecules/NextPrevButtons";
import * as Yup from "yup";
import { postConsentRequestType, UserStatus } from "../../../../types/welcome";
import {
  cancelSignupEmailVerification,
  cancelSignupPhoneNumberVerification,
  requestSignupEmailVerification,
  requestSignupPhoneNumberVerification,
  resendSignupEmailVerification,
  resendSignupPhoneNumberVerification,
  validateUserInfo,
  verifySignupEmailVerification,
  verifySignupPhoneNumberVerification,
} from "../../../../services/user.service";
import { useAuth } from "../../../../contexts/AuthContext";
import { useConfig } from "../../../../contexts/ConfigContext";
import { useHomeContext } from "../../../../pages/Home/HomeContent";
import { useResonanceLayout } from '../../ResonanceContext/ResonanceLayoutContext';
import useToast from "../../../../hooks/useToast";
import { otpChallengeStatusResponse } from "../../../../types/user.types";
import SignupContactVerificationModal, { SignupContactVerificationSection } from "./SignupContactVerificationModal";

const PHONE_NUMBER_PATTERN = /(^06|^07)[0-9]*$/;

type VerificationChannel = "email" | "phone_number";

interface ContactVerificationState {
  email?: string;
  phone_number?: string;
}

interface DisplayInscriptionUserInfo extends postConsentRequestType {
  contact_verification?: ContactVerificationState;
}

interface VerificationFlowState {
  user: any;
  identification_questions: any[];
  channels: VerificationChannel[];
  otpStatusByChannel: Partial<Record<VerificationChannel, otpChallengeStatusResponse | null>>;
}

const normalizeEmail = (value?: string): string => value?.trim().toLowerCase() || "";
const normalizePhoneNumber = (value?: string): string => value?.replace(/\s+/g, "").trim() || "";

interface DisplayInscriptionProps {
  onNextClick: (data: any) => void;
  saveUserInfos: (values: any) => void;
  userInfo: DisplayInscriptionUserInfo;
}

const DisplayInscription = ({
  userInfo,
  saveUserInfos,
  onNextClick,
}: DisplayInscriptionProps): ReactElement => {
  const { t } = useTranslation();

  const { selectedRole } = useConfig();
  const { welcomeData, goPrevious } = useHomeContext();

  const studyId = welcomeData.id;
  const access = selectedRole.access;
  const invitations = welcomeData.invitations;
  const onPreviousClick = goPrevious;
  const { user: userAuth } = useAuth();
  const present = useToast();
  const [verifiedContacts, setVerifiedContacts] = useState<ContactVerificationState>(() => userInfo?.contact_verification || {});
  const [verificationFlow, setVerificationFlow] = useState<VerificationFlowState | null>(null);
  const shouldRequireAtLeastOneContact = useMemo(
    () => hasRoleAccessAtLeastOneOfConstraint(access, ["email", "phonenumber"]),
    [access]
  );
  const shouldAutoSubmitWithoutIdentification = useMemo(
    () =>
      isNoSignInMethod(access.signInMode) &&
      !isRoleAccessFieldEnabled(access, "firstname") &&
      !isRoleAccessFieldEnabled(access, "lastname") &&
      !isRoleAccessFieldEnabled(access, "nickname") &&
      !isRoleAccessFieldEnabled(access, "external_id") &&
      !isRoleAccessFieldEnabled(access, "email") &&
      !isRoleAccessFieldEnabled(access, "phonenumber") &&
      !isRoleAccessFieldEnabled(access, "address"),
    [access]
  );

  useEffect(() => {
    setVerifiedContacts(userInfo?.contact_verification || {});
  }, [userInfo?.contact_verification]);

  const getContactValue = (user: any, channel: VerificationChannel): string => {
    if (channel === "email") {
      return user?.email || "";
    }

    return user?.phone_number || "";
  };

  // Per-role signup verification switches (set by the researcher in Diapason). Absent/true ⇒
  // verify that channel with a 6-digit code; false ⇒ skip it and let signup finish without a code.
  const isEmailVerificationEnabled = selectedRole?.attributes?.is_email_verification_required !== false;
  const isPhoneVerificationEnabled = selectedRole?.attributes?.is_phone_verification_required !== false;

  const getChannelsNeedingVerification = (
    user: any,
    contactVerification: ContactVerificationState
  ): VerificationChannel[] => {
    const channels: VerificationChannel[] = [];

    if (
      isEmailVerificationEnabled &&
      user?.email &&
      normalizeEmail(user.email) !== normalizeEmail(contactVerification.email)
    ) {
      channels.push("email");
    }

    if (
      isPhoneVerificationEnabled &&
      user?.phone_number &&
      normalizePhoneNumber(user.phone_number) !== normalizePhoneNumber(contactVerification.phone_number)
    ) {
      channels.push("phone_number");
    }

    return channels;
  };

  const isChannelVerified = (
    user: any,
    channel: VerificationChannel,
    contactVerification: ContactVerificationState
  ): boolean => {
    const value = getContactValue(user, channel);

    if (channel === "email") {
      return normalizeEmail(value) === normalizeEmail(contactVerification.email);
    }

    return normalizePhoneNumber(value) === normalizePhoneNumber(contactVerification.phone_number);
  };

  const areAllChannelsVerified = (
    user: any,
    channels: VerificationChannel[],
    contactVerification: ContactVerificationState
  ): boolean => channels.every((channel) => isChannelVerified(user, channel, contactVerification));

  const persistInscriptionDraft = (
    user: any,
    identification_questions: any[],
    contactVerification: ContactVerificationState = verifiedContacts
  ) => {
    saveUserInfos({ user, identification_questions, contact_verification: contactVerification });
  };

  const finalizeInscription = (
    user: any,
    identification_questions: any[],
    contactVerification: ContactVerificationState = verifiedContacts
  ) => {
    persistInscriptionDraft(user, identification_questions, contactVerification);
    onNextClick({ user, identification_questions, userAuth });
  };

  const mapVerificationRequestError = (channel: VerificationChannel, error: any) => {
    const responseMessage = String(error?.response?.data?.message || "").toLowerCase();

    if (responseMessage.includes("email already exists")) {
      return {
        field: "email",
        translationKey: "home_inscription_error_EMAIL_EXIST",
      };
    }

    if (responseMessage.includes("phone number already exists")) {
      return {
        field: "phone_number",
        translationKey: "profile_change_phone_exists",
      };
    }

    return {
      field: null,
      translationKey:
        channel === "email"
          ? "home_inscription_email_verification_request_error"
          : "home_inscription_phone_verification_request_error",
    };
  };

  const requestContactVerification = async (
    channel: VerificationChannel,
    user: any
  ): Promise<otpChallengeStatusResponse> => {
    if (channel === "email") {
      const { data } = await requestSignupEmailVerification(
        { studyId },
        { email: user.email }
      );
      return data;
    }

    const { data } = await requestSignupPhoneNumberVerification(
      { studyId },
      { phone_number: user.phone_number }
    );
    return data;
  };

  const cancelContactVerification = async (channel: VerificationChannel) => {
    if (channel === "email") {
      await cancelSignupEmailVerification({ studyId });
      return;
    }

    await cancelSignupPhoneNumberVerification({ studyId });
  };

  const applyVerificationRequestError = (channel: VerificationChannel, error: any) => {
    const mappedError = mapVerificationRequestError(channel, error);

    if (mappedError.field) {
      setErrors({
        ...errors,
        [mappedError.field]: t(mappedError.translationKey),
      });
      return;
    }

    present(t(mappedError.translationKey));
  };

  const requestContactVerifications = async (
    channels: VerificationChannel[],
    user: any
  ): Promise<Partial<Record<VerificationChannel, otpChallengeStatusResponse | null>>> => {
    const otpStatusByChannel: Partial<Record<VerificationChannel, otpChallengeStatusResponse | null>> = {};
    const requestedChannels: VerificationChannel[] = [];

    for (const channel of channels) {
      try {
        otpStatusByChannel[channel] = await requestContactVerification(channel, user);
        requestedChannels.push(channel);
      } catch (error: any) {
        await Promise.all(
          requestedChannels.map((requestedChannel) =>
            cancelContactVerification(requestedChannel).catch(() => undefined)
          )
        );

        throw { channel, error };
      }
    }

    return otpStatusByChannel;
  };

  const startVerificationFlow = async (user: any, identification_questions: any[]) => {
    const channels = getChannelsNeedingVerification(user, verifiedContacts);

    if (!channels.length) {
      finalizeInscription(user, identification_questions);
      return;
    }

    try {
      const otpStatusByChannel = await requestContactVerifications(channels, user);
      setVerificationFlow({
        user,
        identification_questions,
        channels,
        otpStatusByChannel,
      });
    } catch (error: any) {
      applyVerificationRequestError(error?.channel || channels[0], error?.error || error);
    }
  };

  const onSubmit = async (values) => {
    try {
      const user: any = {};
      const identification_questions = [];
      const firstname = values.firstname?.trim();
      const lastname = values.lastname?.trim();
      const nickname = values.nickname?.trim().toLowerCase();
      const email = values.email?.trim();
      const phoneNumber = values.phone_number?.trim();
      const externalId = values.external_id?.trim();
      const address = values.address?.trim();

      if (isRoleAccessFieldEnabled(access, "firstname") && firstname) user.firstname = firstname;
      if (isRoleAccessFieldEnabled(access, "lastname") && lastname) user.lastname = lastname;
      if (isRoleAccessFieldEnabled(access, "nickname") && nickname) {user.nickname = nickname;}
      if (isRoleAccessFieldEnabled(access, "email") && email) user.email = email;
      if (isRoleAccessFieldEnabled(access, "phonenumber") && phoneNumber) user.phone_number = phoneNumber;
      if (isRoleAccessFieldEnabled(access, "external_id") && externalId) user.external_id = externalId;
      if (isRoleAccessFieldEnabled(access, "address") && address) user.address = address;

      if (
        invitations?.to_user?.user_status !==
        UserStatus.INCLUSION_EN_ATTENTE_DE_VALIDATION
      ) {
        if (access.signInMode === IdentificationMethodEnum.SECRET_QUESTIONS) {
          access.secretQuestions.forEach((question) => {
            identification_questions.push({
              id: question.id,
              answer: values[question.id],
              version: 1,
            });
          });
        }

        if (isPasswordSignInMethod(access.signInMode)) {
          user.password = values.password;
        }
      }

      await validateUserInfo(
        { studyId: studyId },
        // external_id rides the same pre-check: validated against the study's allowlist
        // of numéros de dossier (no-op for studies without an imported list).
        { email: user.email, nickname: user.nickname, external_id: user.external_id }
      );

      await startVerificationFlow(user, identification_questions);
    } catch (error: any) {
      const response = error?.response;

      if (!response) {
        console.error(error);
        return;
      }

      switch (response.status) {
        case 400:
          if (response.data) {
            const nextErrors: Record<string, string> = {};

            if (response.data.email != null) {
              nextErrors.email = t("home_inscription_error_" + response.data.email);
            }

            if (response.data.nickname != null) {
              nextErrors.nickname = t("home_inscription_error_" + response.data.nickname);
            }

            if (response.data.external_id != null) {
              nextErrors.external_id = t("home_inscription_error_" + response.data.external_id);
            }

            if (Object.keys(nextErrors).length > 0) {
              setErrors(nextErrors);
            }
          }
          break;
        default:
          console.error(error);
          break;
      }
    }
  };

  const initialValues = useMemo(
    function () {
      let obj: any = {
        firstname: "",
        lastname: "",
        email: "",
        phone_number: "",
        nickname: "",
        external_id: "",
        address: "",
      };

      if (
        invitations?.to_user?.user_status !==
        UserStatus.INCLUSION_EN_ATTENTE_DE_VALIDATION
      ) {
        if (access.signInMode === IdentificationMethodEnum.SECRET_QUESTIONS) {
          access.secretQuestions.forEach((question) => {
            obj[question.id] = "";
          });
        }

        if (isPasswordSignInMethod(access.signInMode)) {
          obj.password = "";
          obj.confirmPassword = "";
        }
      }

      // Prefill from a personal invitation (investigator / intervenor / subject). Only seed
      // fields the invite actually carries; placed before the userInfo spread so the user's
      // own edits win on Back→Forward. No-op for invites that carry no identity (e.g. direct
      // inclusion).
      const invited = invitations?.to_user;
      if (invited) {
        if (invited.firstname) obj.firstname = invited.firstname;
        if (invited.lastname) obj.lastname = invited.lastname;
        if (invited.email) obj.email = invited.email;
        if (invited.phone_number) obj.phone_number = invited.phone_number;
        if (invited.external_id) obj.external_id = invited.external_id;
      }

      if (userInfo?.user) obj = { ...obj, ...userInfo.user };
      obj.confirmPassword = obj.password;

      if (userInfo?.identification_questions) {
        userInfo.identification_questions.forEach((question) => {
          obj[question.id] = question.answer;
        });
      }

      return obj;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [access, invitations?.to_user?.user_status, userInfo]
  );

  const validationSchema = useMemo(
    function () {
      const obj: any = {};

      if (isRoleAccessFieldEnabled(access, "firstname")) {
        let firstnameSchema = Yup.string().trim();

        if (isRoleAccessFieldRequired(access, "firstname")) {
          firstnameSchema = firstnameSchema.required();
        }

        obj.firstname = firstnameSchema;
      }

      if (isRoleAccessFieldEnabled(access, "lastname")) {
        let lastnameSchema = Yup.string().trim();

        if (isRoleAccessFieldRequired(access, "lastname")) {
          lastnameSchema = lastnameSchema.required();
        }

        obj.lastname = lastnameSchema;
      }

      if (isRoleAccessFieldEnabled(access, "nickname")) {
        let nicknameSchema = Yup.string().trim();

        if (isRoleAccessFieldRequired(access, "nickname")) {
          nicknameSchema = nicknameSchema.required();
        }

        obj.nickname = nicknameSchema;
      }

      if (isRoleAccessFieldEnabled(access, "email")) {
        let emailSchema = Yup.string().email("form_error_email");

        if (isRoleAccessFieldRequired(access, "email")) {
          emailSchema = emailSchema.required("form_error_required");
        } else if (shouldRequireAtLeastOneContact) {
          emailSchema = emailSchema.test(
            "either-email-or-phone",
            "form_error_email_or_phone_required",
            function (value) {
              return Boolean(value || this.parent.phone_number);
            }
          );
        }

        obj.email = emailSchema;
      }

      if (isRoleAccessFieldEnabled(access, "phonenumber")) {
        let phoneNumberSchema = Yup.string().matches(PHONE_NUMBER_PATTERN, {
          message: "form_error_phone",
          excludeEmptyString: true,
        });

        if (isRoleAccessFieldRequired(access, "phonenumber")) {
          phoneNumberSchema = phoneNumberSchema.required("form_error_required");
        } else if (shouldRequireAtLeastOneContact) {
          phoneNumberSchema = phoneNumberSchema.test(
            "either-email-or-phone",
            "form_error_email_or_phone_required",
            function (value) {
              return Boolean(value || this.parent.email);
            }
          );
        }

        obj.phone_number = phoneNumberSchema;
      }

      if (isRoleAccessFieldEnabled(access, "external_id")) {
        let externalIdSchema = Yup.string().trim();

        if (isRoleAccessFieldRequired(access, "external_id")) {
          externalIdSchema = externalIdSchema.required();
        }

        obj.external_id = externalIdSchema;
      }

      if (isRoleAccessFieldEnabled(access, "address")) {
        let addressSchema = Yup.string().trim();

        if (isRoleAccessFieldRequired(access, "address")) {
          addressSchema = addressSchema.required();
        }

        obj.address = addressSchema;
      }

      if (
        invitations?.to_user?.user_status !==
        UserStatus.INCLUSION_EN_ATTENTE_DE_VALIDATION
      ) {
        if (access.signInMode === IdentificationMethodEnum.SECRET_QUESTIONS) {
          access.secretQuestions.forEach((question) => {
            obj[question.id] = Yup.string().required();
          });
        }

        if (isPasswordSignInMethod(access.signInMode)) {
          obj.password = Yup.string()
            .min(8)
            .matches(
              /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9])(?!.*\s).{8,100}$/,
              "form_error_email_password"
            )
            .required();
          obj.confirmPassword = Yup.string()
            .required()
            .oneOf([Yup.ref("password")], "form_error_email_confirm_password");
        }
      }

      return Yup.object().shape(obj);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [access, invitations?.to_user?.user_status, shouldRequireAtLeastOneContact]
  );

  const { values, touched, errors, submitForm, setFieldValue, setErrors } =
    useFormik({
      initialValues,
      onSubmit,
      validationSchema,
    });

  const verificationSections: SignupContactVerificationSection[] = useMemo(() => {
    if (!verificationFlow) {
      return [];
    }

    return verificationFlow.channels.map((channel) => ({
      channel,
      value: getContactValue(verificationFlow.user, channel),
      otpStatus: verificationFlow.otpStatusByChannel[channel] || null,
      isVerified: isChannelVerified(verificationFlow.user, channel, verifiedContacts),
    }));
  }, [verificationFlow, verifiedContacts]);

  const handleVerificationConfirm = async (
    channel: VerificationChannel,
    code: string
  ) => {
    if (!verificationFlow) {
      return;
    }

    if (channel === "email") {
      await verifySignupEmailVerification({ studyId }, { code });
    } else {
      await verifySignupPhoneNumberVerification({ studyId }, { code });
    }

    const nextVerifiedContacts = {
      ...verifiedContacts,
      [channel]: getContactValue(verificationFlow.user, channel),
    };

    setVerifiedContacts(nextVerifiedContacts);
    persistInscriptionDraft(
      verificationFlow.user,
      verificationFlow.identification_questions,
      nextVerifiedContacts
    );

    if (areAllChannelsVerified(verificationFlow.user, verificationFlow.channels, nextVerifiedContacts)) {
      setVerificationFlow(null);
      present(t("home_inscription_contact_verification_success"));
      finalizeInscription(
        verificationFlow.user,
        verificationFlow.identification_questions,
        nextVerifiedContacts
      );
      return;
    }
  };

  const handleVerificationResend = async (
    channel: VerificationChannel
  ): Promise<otpChallengeStatusResponse> => {
    if (channel === "email") {
      const { data } = await resendSignupEmailVerification({ studyId });
      setVerificationFlow((current) =>
        current
          ? {
              ...current,
              otpStatusByChannel: {
                ...current.otpStatusByChannel,
                email: data,
              },
            }
          : current
      );
      return data;
    }

    const { data } = await resendSignupPhoneNumberVerification({ studyId });
    setVerificationFlow((current) =>
      current
        ? {
            ...current,
            otpStatusByChannel: {
              ...current.otpStatusByChannel,
              phone_number: data,
            },
          }
        : current
    );
    return data;
  };

  const handleVerificationCancel = async () => {
    if (!verificationFlow) {
      return;
    }

    const pendingChannels = verificationFlow.channels.filter(
      (channel) => !isChannelVerified(verificationFlow.user, channel, verifiedContacts)
    );

    await Promise.all(
      pendingChannels.map((channel) => cancelContactVerification(channel).catch(() => undefined))
    );

    setVerificationFlow(null);
  };
    
  useEffect(() => {
    if (shouldAutoSubmitWithoutIdentification) {
      onSubmit({
        firstname: "",
        lastname: "",
        email: "",
        phone_number: "",
        nickname: "",
        external_id: "",
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldAutoSubmitWithoutIdentification])

  const { setResonanceLayout } = useResonanceLayout();

  useEffect(() => {
    setResonanceLayout({ title: "home_inscription_title" });
  }, []);

  return (
    <Container>
      <div>
        <InscriptionFields
          values={values}
          access={access}
          setFieldValue={setFieldValue}
          errors={errors}
          touched={touched}
          // Invited signups inherit the dossier number from whoever invited them; it is
          // shown for reassurance but not editable, so a typo cannot land this person in
          // a different family than the one that invited them.
          lockExternalId={Boolean(invitations?.to_user?.external_id)}
        />

        {invitations?.to_user?.user_status !==
          UserStatus.INCLUSION_EN_ATTENTE_DE_VALIDATION && (
            <InscriptionIdentifier
              setFieldValue={setFieldValue}
              values={values}
              errors={errors}
              touched={touched}
              access={access}
            />
          )}

        <ContactFields
          access={access}
          values={values}
          setFieldValue={setFieldValue}
          errors={errors}
          touched={touched}
        />
      </div>

      {/* <AnonymousData /> */}

      <NextPrevButtons
        onNextClick={submitForm}
        onPreviousClick={onPreviousClick}
      />

      <SignupContactVerificationModal
        isOpen={Boolean(verificationFlow)}
        sections={verificationSections}
        onVerify={handleVerificationConfirm}
        onResend={handleVerificationResend}
        onCancel={handleVerificationCancel}
      />
    </Container>
  );
};

export default DisplayInscription;
